import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/lib/api";

const CARRIERS = [
  { code: "VTP", label: "Viettel Post" },
  { code: "JT", label: "J&T Express" },
  { code: "BEST", label: "BEST Express" },
  { code: "GHN", label: "Giao Hàng Nhanh" },
  { code: "GHTK", label: "Giao Hàng Tiết Kiệm" },
  { code: "NJV", label: "Ninja Van" },
  { code: "VNP", label: "VN Post" },
  { code: "OTHER", label: "Khác" },
];

const carrierLabel = (code: string) =>
  CARRIERS.find((c) => c.code === code)?.label ?? code;

type Settlement = {
  id: number;
  carrier: string;
  code: string;
  settlement_at: string | null;
  settlement_date: string | null;
  total_cod: string | null;
  total_fee: string | null;
  total_received: string | null;
  total_orders: number | null;
  note: string | null;
  uploaded_at: string;
};

type SettlementItem = {
  id: number;
  order_code: string;
  ship_date: string | null;
  deliver_date: string | null;
  cod: string | null;
  fee: string | null;
  received: string | null;
};

type SettlementDetail = Settlement & { items: SettlementItem[] };

type UploadResult = {
  settlement_id: number;
  carrier: string;
  code: string;
  settlement_at: string | null;
  items_parsed: number;
  total_cod: string;
  total_fee: string;
  total_received: string;
  matched_pos_orders: number;
  unmatched_orders: number;
};

type ReconciliationResult = {
  settlement_id: number;
  carrier: string;
  code: string;
  total_items: number;
  matched: number;
  mismatched_amount: number;
  missing_in_pos: number;
  details: {
    mismatched_amount: {
      order_code: string;
      pos_cod: number;
      bangke_cod: number;
      diff: number;
      shop_id?: number;
    }[];
    missing_in_pos: { order_code: string; cod: number }[];
  };
};

const fmtVND = (v: string | number | null) =>
  v === null || v === undefined
    ? "-"
    : Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 0 }) + "đ";

const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleString("vi-VN") : "-");

export function SettlementsPage() {
  const qc = useQueryClient();
  const [carrierFilter, setCarrierFilter] = useState<string>("");
  const [showUpload, setShowUpload] = useState(false);
  const [openedId, setOpenedId] = useState<number | null>(null);

  const listQ = useQuery({
    queryKey: ["settlements", carrierFilter],
    queryFn: async () => {
      const params = carrierFilter ? { carrier: carrierFilter } : {};
      return (await api.get<Settlement[]>("/settlements", { params })).data;
    },
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Bảng kê ĐVVC</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Chứng từ tiền hàng của đơn vị vận chuyển (VTP/J&T/GHN).
          </p>
        </div>
        <button onClick={() => setShowUpload(true)} className="btn-primary">
          + Tải bảng kê
        </button>
      </div>

      <div className="text-sm text-gray-600 mb-3">
        Bảng kê là chứng từ <b>của đơn vị vận chuyển</b> (VTP/J&T/GHN/...), không
        thuộc cửa hàng nào — 1 bảng kê có thể chứa đơn của nhiều shop.
      </div>

      <div className="mb-3 flex items-center gap-2 text-sm">
        <label className="text-gray-600">Đơn vị vận chuyển:</label>
        <select
          value={carrierFilter}
          onChange={(e) => setCarrierFilter(e.target.value)}
          className="border rounded px-2 py-1"
        >
          <option value="">— Tất cả —</option>
          {CARRIERS.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2">ĐVVC</th>
              <th className="text-left px-3 py-2">Mã bảng kê</th>
              <th className="text-left px-3 py-2">Ngày đối soát</th>
              <th className="text-right px-3 py-2">Số đơn</th>
              <th className="text-right px-3 py-2">Tổng COD</th>
              <th className="text-right px-3 py-2">Tiền cước</th>
              <th className="text-right px-3 py-2">Thu về</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  Đang tải...
                </td>
              </tr>
            )}
            {listQ.data?.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  Chưa có bảng kê nào. Nhấn "+ Tải bảng kê" để upload.
                </td>
              </tr>
            )}
            {listQ.data?.map((s) => (
              <tr
                key={s.id}
                className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                onClick={() => setOpenedId(s.id)}
              >
                <td className="px-3 py-2">
                  <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs font-medium">
                    {s.carrier}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{s.code}</td>
                <td className="px-3 py-2">{fmtDate(s.settlement_at)}</td>
                <td className="px-3 py-2 text-right">{s.total_orders ?? "-"}</td>
                <td className="px-3 py-2 text-right">{fmtVND(s.total_cod)}</td>
                <td className="px-3 py-2 text-right">{fmtVND(s.total_fee)}</td>
                <td className="px-3 py-2 text-right font-medium text-green-700">
                  {fmtVND(s.total_received)}
                </td>
                <td className="px-3 py-2 text-blue-600 text-xs">Xem →</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showUpload && (
        <UploadDrawer
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            qc.invalidateQueries({ queryKey: ["settlements"] });
            setShowUpload(false);
          }}
        />
      )}

      {openedId && <DetailDrawer id={openedId} onClose={() => setOpenedId(null)} />}
    </div>
  );
}

type FileRow = {
  file: File;
  code: string;
  settlement_at: string;
  status: "pending" | "uploading" | "success" | "error";
  result?: UploadResult;
  error?: string;
};

// Carrier có mã bảng kê + ngày đối soát nằm sẵn trong file → backend tự lấy.
const AUTO_FROM_FILE = ["BEST"];

// Mã bảng kê gợi ý từ tên file. BEST: "02_10_2026_NO1000153885544" → "NO1000153885544"
// (backend vẫn ghi đè bằng mã đọc trong file).
function suggestCode(filename: string): string {
  const base = filename.replace(/\.xlsx?$/i, "").trim();
  const mBest = base.match(/NO\d{8,}/);
  return mBest ? mBest[0] : base;
}

// Parse settlement_at từ tên file:
// Pattern PCOD-A-NTU<DD>-<MMYY>-... → 20yy-mm-dd 01:00
// Pattern dd_mm_yyyy hoặc dd-mm-yyyy → date
// Fallback: today
function suggestDate(filename: string): string {
  const base = filename.replace(/\.xlsx?$/i, "");
  // J&T pattern: <customer>-<YYYYMMDD>-<MMDD>  → lấy ngày cuối kỳ làm ngày đối soát
  // Ví dụ 028EC48718-20260330-0331 → 2026-03-31
  const mJT = base.match(/(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(?!\d)/);
  if (mJT) {
    const startYear = mJT[1];
    const startMonth = mJT[2];
    const endMonth = mJT[4];
    const endDay = mJT[5];
    // Năm: hầu hết cùng năm; nếu kỳ vắt qua tháng 12→1 thì +1.
    const endYear =
      startMonth === "12" && endMonth === "01"
        ? String(Number(startYear) + 1)
        : startYear;
    return `${endYear}-${endMonth}-${endDay}T01:00`;
  }
  // VTP pattern: NTU<DD>-<MMYY>
  const m1 = base.match(/NTU(\d{2})-(\d{2})(\d{2})/);
  if (m1) {
    const dd = m1[1];
    const mm = m1[2];
    const yy = m1[3];
    return `20${yy}-${mm}-${dd}T01:00`;
  }
  // yyyy-mm-dd (đặt trước dd-mm-yyyy vì cũng 8-chữ-số đầu có thể match nhầm)
  const m3 = base.match(/(\d{4})[_\-](\d{2})[_\-](\d{2})/);
  if (m3) {
    return `${m3[1]}-${m3[2]}-${m3[3]}T01:00`;
  }
  // dd_mm_yyyy or dd-mm-yyyy
  const m2 = base.match(/(\d{2})[_\-](\d{2})[_\-](\d{4})/);
  if (m2) {
    return `${m2[3]}-${m2[2]}-${m2[1]}T01:00`;
  }
  // Today
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(
    t.getDate()
  ).padStart(2, "0")}T01:00`;
}

function UploadDrawer({
  onClose,
  onUploaded,
}: {
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [carrier, setCarrier] = useState("VTP");
  const [rows, setRows] = useState<FileRow[]>([]);
  const [bulkDate, setBulkDate] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const newRows: FileRow[] = Array.from(files).map((f) => ({
      file: f,
      code: suggestCode(f.name),
      settlement_at: suggestDate(f.name),
      status: "pending",
    }));
    setRows((prev) => [...prev, ...newRows]);
  };

  const updateRow = (idx: number, patch: Partial<FileRow>) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx: number) => setRows((rs) => rs.filter((_, i) => i !== idx));

  const applyBulkDate = () => {
    if (!bulkDate) return;
    setRows((rs) => rs.map((r) => ({ ...r, settlement_at: bulkDate })));
  };

  const uploadAll = async () => {
    if (rows.length === 0) return;
    setUploading(true);
    // Tuần tự để hiển thị progress + tránh hammer server.
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].status === "success") continue;
      updateRow(i, { status: "uploading", error: undefined });
      try {
        const fd = new FormData();
        fd.append("carrier", carrier);
        fd.append("code", rows[i].code);
        fd.append("settlement_at", rows[i].settlement_at);
        fd.append("file", rows[i].file);
        const r = await api.post<UploadResult>("/settlements/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        updateRow(i, { status: "success", result: r.data });
      } catch (e: any) {
        updateRow(i, {
          status: "error",
          error: e?.response?.data?.detail ?? String(e),
        });
      }
    }
    setUploading(false);
  };

  const allDone =
    rows.length > 0 && rows.every((r) => r.status === "success" || r.status === "error");
  const hasSuccess = rows.some((r) => r.status === "success");

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50">
      <div className="w-full max-w-3xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Tải bảng kê (nhiều file)</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            ✕
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Đơn vị vận chuyển <span className="text-red-500">*</span>
            </label>
            <select
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              disabled={uploading}
              className="w-full max-w-xs border rounded px-2 py-1.5 text-sm"
            >
              {CARRIERS.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label} ({c.code})
                </option>
              ))}
            </select>
            <div className="text-xs text-gray-500 mt-0.5">
              Áp dụng cho tất cả file trong lần upload này.{" "}
              {carrier === "JT" && (
                <span className="text-blue-600">
                  J&T: hệ thống tự phát hiện đơn hoàn qua "Số tiền phải trả sau cấn
                  trừ" {"<"} 0.
                </span>
              )}
              {carrier === "BEST" && (
                <span className="text-blue-600">
                  BEST: mã phiếu (NO...) và ngày giờ đối soát lấy tự động từ dòng 1 của
                  file; tổng các dòng được kiểm tra khớp với ô tổng trên phiếu.
                </span>
              )}
            </div>
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center bg-gray-50">
            <input
              type="file"
              accept=".xlsx"
              multiple
              disabled={uploading}
              onChange={(e) => {
                handleFiles(e.target.files);
                e.currentTarget.value = "";
              }}
              className="block w-full text-sm cursor-pointer"
            />
            <div className="text-xs text-gray-500 mt-2">
              Chọn nhiều .xlsx cùng lúc. <b>Mã bảng kê = tên file</b> (anh đổi tên file
              khớp mã trên portal). Ngày đối soát parse tự động:
              <code className="mx-1 bg-white px-1 rounded">NTU22-2605</code> (VTP),
              <code className="mx-1 bg-white px-1 rounded">YYYYMMDD-MMDD</code> (J&T),
              BEST đọc từ trong file,
              <code className="mx-1 bg-white px-1 rounded">dd-mm-yyyy</code>.
            </div>
          </div>

          {rows.length > 0 && (
            <>
              {!AUTO_FROM_FILE.includes(carrier) && (
                <div className="flex items-end gap-2 bg-blue-50 border border-blue-200 rounded p-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-0.5">
                      Set ngày đối soát cho TẤT CẢ file
                    </label>
                    <input
                      type="datetime-local"
                      value={bulkDate}
                      onChange={(e) => setBulkDate(e.target.value)}
                      disabled={uploading}
                      className="border rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={applyBulkDate}
                    disabled={uploading || !bulkDate}
                    className="px-3 py-1.5 text-xs border border-blue-400 bg-white rounded hover:bg-blue-100 disabled:opacity-50"
                  >
                    Áp dụng
                  </button>
                </div>
              )}

              <div className="border rounded overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-2 py-1.5">#</th>
                      <th className="text-left px-2 py-1.5">Tên file → Mã bảng kê</th>
                      <th className="text-left px-2 py-1.5 w-44">Ngày đối soát</th>
                      <th className="text-left px-2 py-1.5">Kết quả</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1.5 text-gray-500">{i + 1}</td>
                        <td className="px-2 py-1.5">
                          <input
                            value={r.code}
                            onChange={(ev) => updateRow(i, { code: ev.target.value })}
                            disabled={uploading || r.status === "success"}
                            className="w-full border rounded px-1 py-0.5 font-mono text-xs"
                          />
                          <div className="text-[10px] text-gray-400 mt-0.5 truncate" title={r.file.name}>
                            📎 {r.file.name} ({Math.round(r.file.size / 1024)}KB)
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          {AUTO_FROM_FILE.includes(carrier) ? (
                            <span className="text-gray-500 italic">
                              {r.result?.settlement_at
                                ? fmtDate(r.result.settlement_at)
                                : "Tự lấy từ file"}
                            </span>
                          ) : (
                            <input
                              type="datetime-local"
                              value={r.settlement_at}
                              onChange={(ev) =>
                                updateRow(i, { settlement_at: ev.target.value })
                              }
                              disabled={uploading || r.status === "success"}
                              className="border rounded px-1 py-0.5 text-xs w-full"
                            />
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          {r.status === "pending" && (
                            <span className="text-gray-500">Chờ</span>
                          )}
                          {r.status === "uploading" && (
                            <span className="text-blue-600">Đang tải...</span>
                          )}
                          {r.status === "success" && r.result && (
                            <span className="text-green-700">
                              ✓ {r.result.items_parsed} đơn / {fmtVND(r.result.total_received)}
                            </span>
                          )}
                          {r.status === "error" && (
                            <span className="text-red-600 text-xs" title={r.error}>
                              ✗ {r.error?.slice(0, 50)}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          {r.status !== "uploading" && r.status !== "success" && (
                            <button
                              type="button"
                              onClick={() => removeRow(i)}
                              disabled={uploading}
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {allDone && hasSuccess && (
                <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                  <div className="font-medium text-green-800">
                    ✓ Đã upload {rows.filter((r) => r.status === "success").length} / {rows.length} file
                  </div>
                  {rows.filter((r) => r.status === "error").length > 0 && (
                    <div className="text-orange-700 text-xs mt-1">
                      {rows.filter((r) => r.status === "error").length} file lỗi — xem dòng đỏ ở
                      trên.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div className="flex gap-2 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            >
              {hasSuccess ? "Đóng" : "Huỷ"}
            </button>
            {hasSuccess && allDone && (
              <button
                type="button"
                onClick={onUploaded}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                Xong, refresh danh sách
              </button>
            )}
            {!allDone && (
              <button
                type="button"
                onClick={uploadAll}
                disabled={uploading || rows.length === 0}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60"
              >
                {uploading
                  ? "Đang upload..."
                  : `Tải lên ${rows.filter((r) => r.status !== "success").length} file`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const detailQ = useQuery({
    queryKey: ["settlement", id],
    queryFn: async () => (await api.get<SettlementDetail>(`/settlements/${id}`)).data,
  });
  const reconQ = useQuery({
    queryKey: ["settlement-recon", id],
    queryFn: async () =>
      (await api.get<ReconciliationResult>(`/settlements/${id}/reconciliation`)).data,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50">
      <div className="w-full max-w-4xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">
              <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs font-medium mr-2">
                {detailQ.data?.carrier}
              </span>
              <span className="font-mono">{detailQ.data?.code}</span>
            </h2>
            <div className="text-sm text-gray-500">
              {carrierLabel(detailQ.data?.carrier ?? "")} • Đối soát:{" "}
              {fmtDate(detailQ.data?.settlement_at ?? null)}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            ✕
          </button>
        </div>

        {detailQ.data && (
          <>
            <div className="grid grid-cols-4 gap-2 mb-4 text-sm">
              <div className="bg-gray-50 rounded p-2">
                <div className="text-gray-500 text-xs">Số đơn</div>
                <div className="font-medium">{detailQ.data.total_orders}</div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <div className="text-gray-500 text-xs">Tổng COD</div>
                <div className="font-medium">{fmtVND(detailQ.data.total_cod)}</div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <div className="text-gray-500 text-xs">Tiền cước</div>
                <div className="font-medium">{fmtVND(detailQ.data.total_fee)}</div>
              </div>
              <div className="bg-green-50 rounded p-2">
                <div className="text-gray-500 text-xs">Thu về</div>
                <div className="font-medium text-green-700">
                  {fmtVND(detailQ.data.total_received)}
                </div>
              </div>
            </div>

            {reconQ.data && (
              <div className="mb-4 grid grid-cols-3 gap-2 text-sm">
                <div className="bg-green-50 border border-green-200 rounded p-2">
                  <div className="text-green-700 text-xs">Khớp</div>
                  <div className="font-medium text-green-800">
                    {reconQ.data.matched} đơn
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                  <div className="text-yellow-700 text-xs">COD lệch</div>
                  <div className="font-medium text-yellow-800">
                    {reconQ.data.mismatched_amount} đơn
                  </div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded p-2">
                  <div className="text-orange-700 text-xs">Không có ở POScake</div>
                  <div className="font-medium text-orange-800">
                    {reconQ.data.missing_in_pos} đơn
                  </div>
                </div>
              </div>
            )}

            {(() => {
              // Phân loại đơn: VTP dựa vào prefix mã (CHPKE/CTPPKE/1P1),
              // JT/BEST dựa vào cod=0 + received<0 = hoàn.
              const isJT = ["JT", "BEST"].includes(detailQ.data.carrier);
              const items = detailQ.data.items;
              const n_return = items.filter((it) => {
                if (isJT) {
                  return Number(it.cod) === 0 && Number(it.received) < 0;
                }
                return (
                  it.order_code.startsWith("CHPKE") ||
                  it.order_code.startsWith("CTPPKE") ||
                  /1P\d+$/.test(it.order_code)
                );
              }).length;
              const n_cod_collect = items.filter((it) => Number(it.cod) > 0).length;
              const n_ship_only = items.filter(
                (it) => Number(it.cod) === 0 && Number(it.received) === 0 && Number(it.fee) === 0
              ).length;
              const n_other = items.length - n_cod_collect - n_return - n_ship_only;
              return (
                <div className="text-xs text-gray-600 mb-3 flex flex-wrap gap-3">
                  <span>
                    Tổng <b>{items.length}</b> dòng
                  </span>
                  <span className="text-green-700">
                    Thu COD: <b>{n_cod_collect}</b>
                  </span>
                  <span className="text-yellow-700">
                    Trừ ship (chờ thu COD): <b>{n_other}</b>
                  </span>
                  <span className="text-red-700">
                    Trả/hoàn (mất ship): <b>{n_return}</b>
                  </span>
                </div>
              );
            })()}
            <table className="w-full text-xs border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-2 py-1">Mã đơn</th>
                  <th className="text-left px-2 py-1">Loại</th>
                  <th className="text-left px-2 py-1">Ngày gửi</th>
                  <th className="text-left px-2 py-1">Ngày phát</th>
                  <th className="text-right px-2 py-1">COD</th>
                  <th className="text-right px-2 py-1">Cước</th>
                  <th className="text-right px-2 py-1">Thu về</th>
                </tr>
              </thead>
              <tbody>
                {detailQ.data.items.map((it) => {
                  const isJT = ["JT", "BEST"].includes(detailQ.data.carrier);
                  const isReturn =
                    it.order_code.startsWith("CHPKE") || it.order_code.startsWith("CTPPKE");
                  const isPartialReturn = /1P\d+$/.test(it.order_code);
                  const isJTReturn =
                    isJT && Number(it.cod) === 0 && Number(it.received) < 0;
                  const isReturnAny = isReturn || isPartialReturn || isJTReturn;
                  const isCodCollect = Number(it.cod) > 0;
                  return (
                    <tr
                      key={it.id}
                      className={`border-t border-gray-100 ${
                        isReturnAny ? "bg-red-50" : isCodCollect ? "bg-green-50/40" : ""
                      }`}
                    >
                      <td className="px-2 py-1 font-mono">{it.order_code}</td>
                      <td className="px-2 py-1">
                        {isReturn || isJTReturn ? (
                          <span className="inline-block bg-red-100 text-red-800 px-1.5 py-0.5 rounded text-[10px]">
                            Hoàn cả đơn
                          </span>
                        ) : isPartialReturn ? (
                          <span className="inline-block bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded text-[10px]">
                            Trả 1 phần
                          </span>
                        ) : isCodCollect ? (
                          <span className="inline-block bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-[10px]">
                            Thu COD
                          </span>
                        ) : (
                          <span className="inline-block bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded text-[10px]">
                            Trừ ship
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1">{fmtDate(it.ship_date)}</td>
                      <td className="px-2 py-1">
                        {it.deliver_date ? fmtDate(it.deliver_date) : "—"}
                      </td>
                      <td className="px-2 py-1 text-right">{fmtVND(it.cod)}</td>
                      <td className="px-2 py-1 text-right">{fmtVND(it.fee)}</td>
                      <td
                        className={`px-2 py-1 text-right ${
                          Number(it.received) < 0 ? "text-red-600" : "text-green-700"
                        }`}
                      >
                        {fmtVND(it.received)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
